import { Text, Stack, Checkbox, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Task } from '../../../../types';
import { useGeoMarkupStore } from '../../../../store';
import { requirementsService } from '../../../../services/api/requirementsService';
import { ProgressSection } from '../../../ui/ProgressSection';
import styles from './TaskRequirements.module.css';

interface TaskRequirementsProps {
  task: Task;
  readOnly?: boolean;
}

export const TaskRequirements = ({
  task,
  readOnly = false,
}: TaskRequirementsProps) => {
  const updateRequirement = useGeoMarkupStore(
    (state) => state.tasks.updateRequirement
  );
  const { t } = useTranslation();

  if (task.requirements.length === 0) {
    return (
      <Box className={styles.container}>
        <Text size="sm" c="dimmed" ta="center" py="xl">
          {t('requirements.noRequirements')}
        </Text>
      </Box>
    );
  }

  const completedRequirements = task.requirements.filter(
    (req) => req.completed
  ).length;
  const totalRequirements = task.requirements.length;

  const handleRequirementToggle = async (requirementId: string) => {
    const currentRequirement = task.requirements.find(
      (req) => req.id === requirementId
    );
    if (!currentRequirement) return;

    const newCompleted = !currentRequirement.completed;

    updateRequirement(task.id, requirementId, { completed: newCompleted });

    try {
      await requirementsService.update(task.id, requirementId, {
        isCompleted: newCompleted,
      });
    } catch {
      updateRequirement(task.id, requirementId, {
        completed: currentRequirement.completed,
      });
    }
  };

  return (
    <Box className={styles.container}>
      <ProgressSection
        title={t('requirements.progress')}
        completed={completedRequirements}
        total={totalRequirements}
        className={styles.progressSection}
      />

      <Box className={styles.requirementsList}>
        <Stack gap="xs">
          {[...task.requirements]
            .sort((a, b) => a.order - b.order)
            .map((requirement) => (
              <Box
                key={requirement.id}
                className={`${styles.requirementItem} ${readOnly ? styles.requirementItemReadOnly : ''}`}
                onClick={
                  readOnly
                    ? undefined
                    : () => handleRequirementToggle(requirement.id)
                }
              >
                <Checkbox
                  checked={requirement.completed}
                  onChange={() => handleRequirementToggle(requirement.id)}
                  onClick={(event) => event.stopPropagation()}
                  disabled={readOnly}
                />
                <Text
                  size="sm"
                  className={`${styles.requirementText} ${requirement.completed ? styles.requirementTextCompleted : ''}`}
                >
                  {requirement.text}
                </Text>
              </Box>
            ))}
        </Stack>
      </Box>
    </Box>
  );
};
